import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  useSlotGroup,
  useProperty,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// NeighborhoodHighlights — BY DAY / BY NIGHT points-of-interest sliders
// ----------------------------------------------------------------------------
// Ported from the Aion Management Stadium House theme for the Belmont
// Neighborhood page. Visual pattern:
//   - A segmented toggle pill on a white section band, with sun / moon
//     glyphs on the BY DAY / BY NIGHT tabs. The track uses the theme
//     surface color; the active half is filled with primary.
//   - Cards sit on the property primary color; night cards swap to a
//     lightened-primary surface so the two moods read distinctly.
//   - Outlined round prev/next arrows aligned top-right with the Day/Night
//     filter so the controls read as one intentional toolbar.
//
// Each card is its own editable slot group with a `neighborhood_pois`
// default binding and a generator hint, so an editor can hand-pick which
// POIs headline the daytime vs. nighttime experience (the POI data model
// has no time-of-day dimension, so the split has to be authored).
// ============================================================================

function hasVal(v: string | null | undefined): v is string {
  return !!v && v !== 'false' && v !== 'null' && v !== 'undefined' && v.trim() !== '';
}

function formatDistance(raw: string): string {
  const t = raw.trim();
  return /(mi|ft|km|min)$/i.test(t) || /min/i.test(t) ? t : `${t} mi`;
}

function normalizePoiName(name: string): string {
  return name.trim().toLowerCase();
}

// ---------------------------------------------------------------------------
// Color helpers — round-trip the property primary color through HSL and bump
// lightness by a few points to derive the night-card surface, so the night
// tiles read as "one tier brighter than the section band" regardless of which
// property is rendering the theme.
// ---------------------------------------------------------------------------
function hueToHex(hue: number, sat: number, light: number): string {
  const h = hue / 360, s = sat / 100, l = light / 100;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return { h: 200, s: 50, l: 50 };
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function lighten(hex: string, amount: number): string {
  const { h, s, l } = hexToHsl(hex);
  const nextL = Math.min(100, Math.max(0, l + amount));
  return hueToHex(h, s, nextL);
}

// Inline icon glyphs for the toggle pill. `currentColor` fill follows the
// button's text color so they flip with the active/inactive state.
function SunIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
      <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

// ============================================================================
// Slot group definitions — fully-inlined literals so the preview system's
// static schema extractor can resolve them (no shared factory / referenced
// consts). Day cards default to POIs 0–3 + 8–9, night cards to POIs 4–7 + 10–11
// (new cards append at the end of the collection so existing day/night picks
// stay on their original indices).
// ============================================================================

const dayPoi1Def: SlotGroupDefinition = {
  label: 'Daytime Spot 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[0].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[0].poi_name', defaultValue: 'Corner Coffee House' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[0].poi_type', defaultValue: 'Cafe' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[0].poi_miles_from_property', defaultValue: '0.3 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[0].poi_short_description', defaultValue: 'Start your morning with locally roasted espresso and fresh pastries just around the corner.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 0,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Daytime slider, card 1 of 6 — a lively daytime spot (cafe, coffee, breakfast). Each of the 6 daytime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const dayPoi2Def: SlotGroupDefinition = {
  label: 'Daytime Spot 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[1].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[1].poi_name', defaultValue: 'Midtown Market' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[1].poi_type', defaultValue: 'Shopping' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[1].poi_miles_from_property', defaultValue: '0.6 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[1].poi_short_description', defaultValue: 'Browse boutique shops and artisan goods along the neighborhood’s bustling main street.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 1,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Daytime slider, card 2 of 6 — a daytime shopping or retail destination. Each of the 6 daytime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const dayPoi3Def: SlotGroupDefinition = {
  label: 'Daytime Spot 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[2].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[2].poi_name', defaultValue: 'Riverside Park' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[2].poi_type', defaultValue: 'Park' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[2].poi_miles_from_property', defaultValue: '0.4 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[2].poi_short_description', defaultValue: 'Green lawns, walking trails and weekend markets make this the go-to daytime escape.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 2,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Daytime slider, card 3 of 6 — an outdoor / park / green space for the daytime. Each of the 6 daytime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const dayPoi4Def: SlotGroupDefinition = {
  label: 'Daytime Spot 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[3].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[3].poi_name', defaultValue: 'The Daily Kitchen' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[3].poi_type', defaultValue: 'Restaurant' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[3].poi_miles_from_property', defaultValue: '0.5 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[3].poi_short_description', defaultValue: 'A sunny brunch spot serving seasonal plates and fresh-pressed juices all afternoon.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 3,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Daytime slider, card 4 of 6 — a daytime dining / brunch spot. Each of the 6 daytime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const dayPoi5Def: SlotGroupDefinition = {
  label: 'Daytime Spot 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[8].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[8].poi_name', defaultValue: 'Gallery Row' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[8].poi_type', defaultValue: 'Arts' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[8].poi_miles_from_property', defaultValue: '0.9 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[8].poi_short_description', defaultValue: 'Wander local galleries and studios showcasing emerging artists from the neighborhood.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 8,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Daytime slider, card 5 of 6 — an arts, culture, or museum stop for daytime exploring. Each of the 6 daytime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const dayPoi6Def: SlotGroupDefinition = {
  label: 'Daytime Spot 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[9].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[9].poi_name', defaultValue: 'Harbor Trail' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[9].poi_type', defaultValue: 'Recreation' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[9].poi_miles_from_property', defaultValue: '1.1 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[9].poi_short_description', defaultValue: 'A scenic waterfront path perfect for a midday jog, bike ride, or leisurely stroll.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 9,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Daytime slider, card 6 of 6 — a recreation / fitness / trail destination. Each of the 6 daytime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const nightPoi1Def: SlotGroupDefinition = {
  label: 'Nighttime Spot 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[4].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[4].poi_name', defaultValue: 'Lumen Cocktail Bar' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[4].poi_type', defaultValue: 'Nightlife' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[4].poi_miles_from_property', defaultValue: '0.4 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[4].poi_short_description', defaultValue: 'Craft cocktails and a low-lit ambiance for an intimate evening out.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 4,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Nighttime slider, card 1 of 6 — a bar / cocktail / nightlife spot. Each of the 6 nighttime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const nightPoi2Def: SlotGroupDefinition = {
  label: 'Nighttime Spot 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[5].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[5].poi_name', defaultValue: 'The Velvet Room' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[5].poi_type', defaultValue: 'Live Music' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[5].poi_miles_from_property', defaultValue: '0.7 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[5].poi_short_description', defaultValue: 'Catch local bands and DJs in this beloved after-dark music venue.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 5,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Nighttime slider, card 2 of 6 — a live music / entertainment venue. Each of the 6 nighttime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const nightPoi3Def: SlotGroupDefinition = {
  label: 'Nighttime Spot 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[6].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[6].poi_name', defaultValue: 'Ember Steakhouse' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[6].poi_type', defaultValue: 'Restaurant' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[6].poi_miles_from_property', defaultValue: '0.5 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[6].poi_short_description', defaultValue: 'Candlelit dinners and a curated wine list make for the perfect night out.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 6,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Nighttime slider, card 3 of 6 — an evening dinner / fine-dining spot. Each of the 6 nighttime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const nightPoi4Def: SlotGroupDefinition = {
  label: 'Nighttime Spot 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[7].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[7].poi_name', defaultValue: 'Skyline Lounge' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[7].poi_type', defaultValue: 'Rooftop Bar' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[7].poi_miles_from_property', defaultValue: '0.8 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[7].poi_short_description', defaultValue: 'Sip and unwind with panoramic city views from this rooftop hotspot.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 7,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Nighttime slider, card 4 of 6 — a rooftop / lounge / late-night hangout. Each of the 6 nighttime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const nightPoi5Def: SlotGroupDefinition = {
  label: 'Nighttime Spot 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[10].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[10].poi_name', defaultValue: 'Cinema House' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[10].poi_type', defaultValue: 'Entertainment' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[10].poi_miles_from_property', defaultValue: '0.6 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[10].poi_short_description', defaultValue: 'Catch the latest releases or indie screenings at this cozy neighborhood theater.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 10,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Nighttime slider, card 5 of 6 — a cinema / theater / entertainment venue. Each of the 6 nighttime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

const nightPoi6Def: SlotGroupDefinition = {
  label: 'Nighttime Spot 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'neighborhood_pois[11].poi_featured_image' },
    name: { label: 'Name', type: 'text', defaultField: 'neighborhood_pois[11].poi_name', defaultValue: 'Late Night Bites' },
    category: { label: 'Category', type: 'text', defaultField: 'neighborhood_pois[11].poi_type', defaultValue: 'Late Night Eats' },
    distance: { label: 'Distance', type: 'text', defaultField: 'neighborhood_pois[11].poi_miles_from_property', defaultValue: '0.5 mi' },
    description: { label: 'Description', type: 'text', defaultField: 'neighborhood_pois[11].poi_short_description', defaultValue: 'Satisfy late-night cravings with comfort classics served until the early hours.' },
  },
  sourceBinding: {
    collection: 'neighborhood_pois',
    defaultIndex: 11,
    fieldMappings: { image: 'poi_featured_image', name: 'poi_name', category: 'poi_type', distance: 'poi_miles_from_property', description: 'poi_short_description' },
  },
  generator: {
    prompt: 'Pick a standout nearby point of interest to feature in the Explore the Neighborhood slider.',
    context: 'Nighttime slider, card 6 of 6 — a late-night eat / dessert / after-hours spot. Each of the 6 nighttime cards must be a different POI.',
    sourceCollection: 'neighborhood_pois',
  },
};

type TabKey = 'day' | 'night';

// ============================================================================
// Presentational card (no hooks — lives inside each EditableSlotGroup)
// ============================================================================

interface CardValues {
  image?: string | null;
  name?: string | null;
  category?: string | null;
  distance?: string | null;
  description?: string | null;
}

/**
 * Generators sometimes leave `distance` empty (or it falls back to the
 * slot group's default-index miles, which belongs to a different POI).
 * Match the card name 1:1 against `neighborhood_pois` and use that item's
 * miles. No match → hide the badge. Company-theme preview has no collection,
 * so the authored slot value still shows.
 */
function resolveCardDistance(
  name: string | null | undefined,
  slotDistance: string | null | undefined,
  distanceByName: Map<string, string>,
  hasPoiCollection: boolean,
): string | null {
  if (hasVal(name)) {
    const matched = distanceByName.get(normalizePoiName(name));
    if (hasVal(matched)) return matched;
  }
  if (!hasPoiCollection && hasVal(slotDistance)) return slotDistance;
  return null;
}

function withResolvedDistance(
  card: CardValues,
  distanceByName: Map<string, string>,
  hasPoiCollection: boolean,
): CardValues {
  return {
    ...card,
    distance: resolveCardDistance(card.name, card.distance, distanceByName, hasPoiCollection),
  };
}

function PoiCard({ image, name, category, distance, description }: CardValues) {
  return (
    <>
      <div className="nh-card-media">
        {hasVal(image) ? (
          <VibesitesImage
            url={image}
            alt={name || ''}
            sizes="(min-width: 640px) 300px, 74vw"
            className="nh-card-img"
          />
        ) : (
          <div className="nh-card-img nh-card-img--empty" />
        )}
        {hasVal(distance) && <span className="nh-card-distance">{formatDistance(distance)}</span>}
      </div>
      <div className="nh-card-body">
        {hasVal(category) && <p className="nh-card-type">{category}</p>}
        {hasVal(name) && <h3 className="nh-card-name">{name}</h3>}
        {hasVal(description) && <p className="nh-card-desc">{description}</p>}
      </div>
    </>
  );
}

/**
 * Day / night neighborhood explorer with a segmented toggle pill and two
 * horizontal card sliders. Each card is an editable slot group defaulting to
 * a `neighborhood_pois` entry so editors can hand-pick which spots headline
 * daytime vs. nighttime moods.
 */
export function NeighborhoodHighlights() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const property = useProperty();
  const pois = property.neighborhood_pois;
  const hasPoiCollection = (pois?.length ?? 0) > 0;
  const poiDistanceByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const poi of pois ?? []) {
      if (!hasVal(poi.poi_name) || !hasVal(poi.poi_miles_from_property)) continue;
      const key = normalizePoiName(poi.poi_name);
      if (!map.has(key)) map.set(key, poi.poi_miles_from_property);
    }
    return map;
  }, [pois]);

  // Section background = property primary brand color. The night-card surface
  // is derived from it (one tier lighter) so the night tiles read as a distinct
  // "brighter than the band" surface regardless of which property is rendering.
  const primaryColor = (property?.property_primary_color as string) || '#0a1f2e';
  const nightCardBg = useMemo(() => lighten(primaryColor, 5), [primaryColor]);

  // Resolve each card's editable/generated values.
  const day1 = useSlotGroup('neighborhoodhighlights.day_poi_1', dayPoi1Def);
  const day2 = useSlotGroup('neighborhoodhighlights.day_poi_2', dayPoi2Def);
  const day3 = useSlotGroup('neighborhoodhighlights.day_poi_3', dayPoi3Def);
  const day4 = useSlotGroup('neighborhoodhighlights.day_poi_4', dayPoi4Def);
  const day5 = useSlotGroup('neighborhoodhighlights.day_poi_5', dayPoi5Def);
  const day6 = useSlotGroup('neighborhoodhighlights.day_poi_6', dayPoi6Def);
  const night1 = useSlotGroup('neighborhoodhighlights.night_poi_1', nightPoi1Def);
  const night2 = useSlotGroup('neighborhoodhighlights.night_poi_2', nightPoi2Def);
  const night3 = useSlotGroup('neighborhoodhighlights.night_poi_3', nightPoi3Def);
  const night4 = useSlotGroup('neighborhoodhighlights.night_poi_4', nightPoi4Def);
  const night5 = useSlotGroup('neighborhoodhighlights.night_poi_5', nightPoi5Def);
  const night6 = useSlotGroup('neighborhoodhighlights.night_poi_6', nightPoi6Def);

  const [activeTab, setActiveTab] = useState<TabKey>('day');

  // ── Slider scroll state ─────────────────────────────────────────────────
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft >= max - 2);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    // Reset to the start whenever the active slider changes.
    el.scrollLeft = 0;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, activeTab]);

  const scrollByCard = useCallback((dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('.nh-card');
    const step = card ? card.offsetWidth + 20 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  }, []);

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up nh-section"
      aria-label="Neighborhood highlights"
      style={{ '--nh-night-card-bg': nightCardBg } as CSSProperties}
    >
      <div className="nh-inner">
        {/* Toolbar: Day/Night filter on the left, POI arrows on the right —
            same row so the controls read as one intentional control bar. */}
        <div className="nh-toolbar">
          <div className="nh-tabs" role="tablist" aria-label="Explore the neighborhood by time of day">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'day'}
              className={`nh-tab${activeTab === 'day' ? ' nh-tab--active' : ''}`}
              onClick={() => setActiveTab('day')}
            >
              <SunIcon />
              By Day
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'night'}
              className={`nh-tab${activeTab === 'night' ? ' nh-tab--active' : ''}`}
              onClick={() => setActiveTab('night')}
            >
              <MoonIcon />
              By Night
            </button>
          </div>

          <div className="nh-controls">
            <button
              type="button"
              className="nh-arrow"
              onClick={() => scrollByCard(-1)}
              disabled={atStart}
              aria-label="Previous points of interest"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M11 3.5L5.5 9l5.5 5.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className="nh-arrow"
              onClick={() => scrollByCard(1)}
              disabled={atEnd}
              aria-label="Next points of interest"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M7 3.5L12.5 9L7 14.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        <div className="nh-slider" role="tabpanel" data-slide={activeTab}>
          {activeTab === 'day' ? (
            <div ref={trackRef} className="nh-track" key="day">
              <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_1" definition={dayPoi1Def} className="nh-card">
                <PoiCard {...withResolvedDistance(day1, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_2" definition={dayPoi2Def} className="nh-card">
                <PoiCard {...withResolvedDistance(day2, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_3" definition={dayPoi3Def} className="nh-card">
                <PoiCard {...withResolvedDistance(day3, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_4" definition={dayPoi4Def} className="nh-card">
                <PoiCard {...withResolvedDistance(day4, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_5" definition={dayPoi5Def} className="nh-card">
                <PoiCard {...withResolvedDistance(day5, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_6" definition={dayPoi6Def} className="nh-card">
                <PoiCard {...withResolvedDistance(day6, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
            </div>
          ) : (
            <div ref={trackRef} className="nh-track" key="night">
              <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_1" definition={nightPoi1Def} className="nh-card">
                <PoiCard {...withResolvedDistance(night1, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_2" definition={nightPoi2Def} className="nh-card">
                <PoiCard {...withResolvedDistance(night2, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_3" definition={nightPoi3Def} className="nh-card">
                <PoiCard {...withResolvedDistance(night3, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_4" definition={nightPoi4Def} className="nh-card">
                <PoiCard {...withResolvedDistance(night4, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_5" definition={nightPoi5Def} className="nh-card">
                <PoiCard {...withResolvedDistance(night5, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_6" definition={nightPoi6Def} className="nh-card">
                <PoiCard {...withResolvedDistance(night6, poiDistanceByName, hasPoiCollection)} />
              </EditableSlotGroup>
            </div>
          )}
        </div>
      </div>

      {/* Schema-lint anchors — hidden, never interactive. Ensures every schema
          slot group has a matching wrapper somewhere in the tree even when the
          user is looking at the opposite tab (only one slider is mounted at a
          time above). */}
      <div style={{ display: 'none' }} aria-hidden="true">
        <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_1" definition={dayPoi1Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_2" definition={dayPoi2Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_3" definition={dayPoi3Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_4" definition={dayPoi4Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_5" definition={dayPoi5Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.day_poi_6" definition={dayPoi6Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_1" definition={nightPoi1Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_2" definition={nightPoi2Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_3" definition={nightPoi3Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_4" definition={nightPoi4Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_5" definition={nightPoi5Def}><span /></EditableSlotGroup>
        <EditableSlotGroup groupPath="neighborhoodhighlights.night_poi_6" definition={nightPoi6Def}><span /></EditableSlotGroup>
      </div>

      <style>{`
        .nh-section {
          /* White band. Day cards get a subtle border since they can no longer
             rely on contrast against a dark background; night cards keep their
             lightened-primary surface so the two moods still read distinctly.
             Break out of any centered / max-width parent so the band spans the
             full viewport regardless of ancestor padding. */
          background: #ffffff;
          color: #1a1a1a;
          padding: 3rem 0;
          width: 100vw;
          margin-left: calc(50% - 50vw);
          margin-right: calc(50% - 50vw);
          box-sizing: border-box;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .nh-section { padding: 4.5rem 0; }
        }
        .nh-inner {
          max-width: 78rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .nh-inner { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .nh-inner { padding: 0 2rem; } }

        /* ── Toolbar: Day/Night filter left, POI arrows right ── */
        .nh-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin: 0 0 2.25rem;
        }

        /* ── Day / Night toggle — segmented pill on the theme surface color ──
           The full pill background uses --vibe-color-surface; the active half
           is filled with the primary color. Inactive tab ink is white so it
           stays readable against the dark surface pill. */
        .nh-tabs {
          display: flex;
          align-items: stretch;
          gap: 0;
          width: fit-content;
          border-radius: 9999px;
          padding: 0;
          background: var(--vibe-color-surface);
          border: none;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin: 0;
        }
        .nh-tab {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-family: var(--vibe-font-body);
          font-size: 0.68rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          white-space: nowrap;
          padding: 0.7rem 0.95rem;
          border: none;
          border-radius: 9999px;
          background: transparent;
          color: #ffffff;
          cursor: pointer;
          transition: background-color 0.2s ease-out, color 0.2s ease-out;
        }
        @media (min-width: 640px) {
          .nh-tab {
            gap: 0.75rem;
            font-size: 0.85rem;
            letter-spacing: 0.18em;
            padding: 0.95rem 2.25rem;
          }
        }
        .nh-tab:hover:not(.nh-tab--active) {
          color: #ffffff;
          opacity: 0.75;
        }
        .nh-tab--active,
        .nh-tab--active:hover {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 1;
        }
        .nh-tab:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }
        .nh-tab svg { flex-shrink: 0; width: 14px; height: 14px; }
        @media (min-width: 640px) {
          .nh-tab svg { width: 18px; height: 18px; }
        }

        /* ── Slider ── */
        .nh-slider { position: relative; }
        .nh-track {
          display: flex;
          gap: 1.25rem;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          padding: 0.5rem 1rem 1.25rem;
          margin: 0 -1rem;
          scroll-padding-left: 1rem;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          animation: nh-fade 0.35s ease both;
        }
        @media (min-width: 640px) {
          .nh-track { margin: 0 -1.5rem; padding-left: 1.5rem; padding-right: 1.5rem; scroll-padding-left: 1.5rem; }
        }
        @media (min-width: 1024px) {
          .nh-track { margin: 0 -2rem; padding-left: 2rem; padding-right: 2rem; scroll-padding-left: 2rem; }
        }
        .nh-track::-webkit-scrollbar { display: none; }

        /* ── Card (the EditableSlotGroup element) ── */
        /* All cards sit on the property primary color for a bold, dark tile
           against the white section band. Night cards keep a subtly lighter
           surface (derived one tier brighter than primary) so the two moods
           still read distinctly. */
        .nh-card {
          scroll-snap-align: start;
          flex: 0 0 74%;
          max-width: 74%;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border-radius: 10px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
        }
        @media (min-width: 640px) { .nh-card { flex-basis: 300px; max-width: 300px; } }
        /* Night cards: swap to the lightened-primary surface. */
        .nh-slider[data-slide="night"] .nh-card {
          background: var(--nh-night-card-bg, var(--vibe-color-primary));
        }

        .nh-card-media {
          position: relative;
          width: 100%;
          aspect-ratio: 4 / 3;
          background: rgba(0, 0, 0, 0.06);
        }
        .nh-card-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .nh-card-img--empty { background: rgba(0, 0, 0, 0.08); }
        .nh-card-distance {
          position: absolute;
          left: 0.75rem;
          bottom: 0.75rem;
          font-family: var(--vibe-font-body);
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          padding: 0.3rem 0.7rem;
          border-radius: 9999px;
          background: rgba(0, 0, 0, 0.62);
          color: #ffffff;
          backdrop-filter: blur(2px);
        }

        .nh-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          padding: 1.15rem 1.25rem 1.35rem;
        }
        .nh-card-type {
          font-family: var(--vibe-font-body);
          font-size: 0.68rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-text-on-primary, #fff);
          opacity: 0.75;
          margin: 0;
        }
        .nh-card-name {
          font-family: var(--vibe-font-heading), sans-serif;
          font-size: 1.25rem;
          font-weight: 700;
          line-height: 1.2;
          color: inherit;
          margin: 0;
        }
        .nh-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          font-weight: 400;
          line-height: 1.6;
          color: inherit;
          opacity: 0.85;
          margin: 0.15rem 0 0;
        }

        /* ── Arrow controls — top-right, aligned with the Day/Night filter ── */
        .nh-controls {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 0.75rem;
          margin-left: auto;
        }
        .nh-arrow {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 2.75rem;
          height: 2.75rem;
          border-radius: 9999px;
          border: 1px solid rgba(0, 0, 0, 0.35);
          background: transparent;
          color: #1a1a1a;
          cursor: pointer;
          transition: opacity 0.2s ease, background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        }
        .nh-arrow:hover:not(:disabled) {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #ffffff);
          border-color: var(--vibe-color-primary);
        }
        .nh-arrow:disabled { opacity: 0.3; cursor: default; }
        .nh-arrow svg { display: block; }

        @keyframes nh-fade {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .nh-track { animation: none; scroll-behavior: auto; }
          .nh-arrow { transition: none; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodHighlights',
  displayName: 'Neighborhood Highlights',
  tokens: {},
  slotGroups: {
    'neighborhoodhighlights.day_poi_1': dayPoi1Def,
    'neighborhoodhighlights.day_poi_2': dayPoi2Def,
    'neighborhoodhighlights.day_poi_3': dayPoi3Def,
    'neighborhoodhighlights.day_poi_4': dayPoi4Def,
    'neighborhoodhighlights.day_poi_5': dayPoi5Def,
    'neighborhoodhighlights.day_poi_6': dayPoi6Def,
    'neighborhoodhighlights.night_poi_1': nightPoi1Def,
    'neighborhoodhighlights.night_poi_2': nightPoi2Def,
    'neighborhoodhighlights.night_poi_3': nightPoi3Def,
    'neighborhoodhighlights.night_poi_4': nightPoi4Def,
    'neighborhoodhighlights.night_poi_5': nightPoi5Def,
    'neighborhoodhighlights.night_poi_6': nightPoi6Def,
  },
};

(NeighborhoodHighlights as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodHighlights;
